// @ts-check

/** @file Compact, nonblocking authenticated-chrome sync status banner with a detail sheet. */

import _ from "gettext-universal/build/src/translate.js"
import {useSyncExternalStore, useState} from "react"
import {Modal, Pressable, Text, View} from "react-native"

import {offlineSyncProviderEnabled, subscribeOfflineSyncProviderState} from "../sync/offline-sync-provider.js"
import {projectSyncStatusStore, useProjectSyncStatus} from "../sync/project-sync-status.js"
import {projectSyncStatusSurface} from "../sync/project-sync-status-presentation.js"

/** @import {ReactElement} from "react" */
/** @import {SyncStatusSurface} from "../sync/project-sync-status-presentation.js" */

export {projectSyncStatusPresentation, projectSyncStatusSurface} from "../sync/project-sync-status-presentation.js"

const TONES = Object.freeze({
  danger: Object.freeze({backgroundColor: "#fee2e2", color: "#991b1b"}),
  info: Object.freeze({backgroundColor: "#dbeafe", color: "#1e40af"}),
  warning: Object.freeze({backgroundColor: "#fef3c7", color: "#92400e"})
})

/**
 * Renders the coordinator's sync state as a nonblocking banner with state label, reason, queued badge, detail sheet and Retry.
 * @returns {ReactElement | null} Compact status control, or null when no banner is needed.
 */
export default function ProjectSyncStatusIndicator() {
  const status = useProjectSyncStatus()
  const providerEnabled = useSyncExternalStore(subscribeOfflineSyncProviderState, offlineSyncProviderEnabled, offlineSyncProviderEnabled)
  const [detailsOpen, setDetailsOpen] = useState(false)
  const surface = projectSyncStatusSurface(status, {enabled: providerEnabled})

  if (!surface) return null

  const tone = TONES[surface.tone]

  return (
    <View
      accessibilityLiveRegion="polite"
      style={{alignItems: "center", backgroundColor: tone.backgroundColor, borderRadius: 999, flexDirection: "row", gap: 6, paddingHorizontal: 9, paddingVertical: 5}}
      testID="projectSyncStatus"
    >
      <Text style={{color: tone.color, fontSize: 11, fontWeight: 700}} testID="projectSyncStatus/label">
        {_(surface.labelKey)}
      </Text>
      {surface.reasonKey &&
        <Text style={{color: tone.color, fontSize: 11}} testID="projectSyncStatus/reason">
          {_(surface.reasonKey)}
        </Text>
      }
      {surface.queueBadgeKey &&
        <Text style={{color: tone.color, fontSize: 11, fontWeight: 700}} testID="projectSyncStatus/queueBadge">
          {_(surface.queueBadgeKey, {count: String(surface.pendingCount)})}
        </Text>
      }
      <Pressable accessibilityRole="button" onPress={() => { setDetailsOpen((open) => !open) }} testID="projectSyncStatus/details">
        <Text style={{color: tone.color, fontSize: 11, fontWeight: 800}}>{_("Details")}</Text>
      </Pressable>
      {surface.retryable &&
        <Pressable accessibilityRole="button" onPress={() => { void retryProjectSync() }} testID="projectSyncStatus/retry">
          <Text style={{color: tone.color, fontSize: 11, fontWeight: 800}}>{_("Retry")}</Text>
        </Pressable>
      }
      {detailsOpen &&
        <SyncStatusDetailSheet
          onClose={() => { setDetailsOpen(false) }}
          onRetry={surface.retryable ? () => { void retryProjectSync() } : null}
          surface={surface}
          tone={tone}
        />
      }
    </View>
  )
}

/**
 * Detail sheet for the projected sync status: reason, queued/rejected counts,
 * conflicts, last success and next retry times, plus the Retry action.
 * @param {{onClose: () => void, onRetry: (() => void) | null, surface: SyncStatusSurface, tone: {backgroundColor: string, color: string}}} args Sheet inputs.
 * @returns {ReactElement} Detail sheet modal.
 */
function SyncStatusDetailSheet({onClose, onRetry, surface, tone}) {
  return (
    <Modal onRequestClose={onClose} transparent visible>
      <View
        style={{backgroundColor: "rgba(0, 0, 0, 0.45)", flex: 1, justifyContent: "center", padding: 24}}
        testID="projectSyncStatus/detailSheetBackdrop"
      >
        <View
          style={{backgroundColor: "#ffffff", borderRadius: 12, gap: 8, maxWidth: 420, padding: 16, width: "100%"}}
          testID="projectSyncStatus/detailSheet"
        >
          <Text style={{color: "#111827", fontSize: 14, fontWeight: 800}} testID="projectSyncStatus/detailSheet/title">
            {_("Sync status")}
          </Text>
          {surface.details.map((row) =>
            <View key={row.testID} testID={`projectSyncStatus/detailSheet/${row.testID}`}>
              <Text style={{color: "#6b7280", fontSize: 11, fontWeight: 700}}>{_(row.labelKey)}</Text>
              <Text style={{color: tone.color, fontSize: 12}} testID={`projectSyncStatus/detailSheet/${row.testID}/text`}>
                {_(row.textKey, row.textParams)}
              </Text>
            </View>
          )}
          {onRetry &&
            <Pressable accessibilityRole="button" onPress={onRetry} testID="projectSyncStatus/detailSheet/retry">
              <Text style={{color: tone.color, fontSize: 12, fontWeight: 800}}>{_("Retry")}</Text>
            </Pressable>
          }
          <Pressable accessibilityRole="button" onPress={onClose} testID="projectSyncStatus/detailSheet/close">
            <Text style={{color: "#6b7280", fontSize: 12, fontWeight: 700}}>{_("Close")}</Text>
          </Pressable>
        </View>
      </View>
    </Modal>
  )
}

/**
 * Handles manual retry without exposing raw failures to the UI or console.
 * @returns {Promise<void>} Retry handling completion.
 */
async function retryProjectSync() {
  try {
    await projectSyncStatusStore.retry()
  } catch (_error) {
    console.warn("Project sync retry did not complete")
  }
}
